import {AxiosError} from "axios";
import classNames from "classnames";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate, useSearchParams} from "react-router-dom";
import {common as stringsCommon, expressRegisterPage as strings} from "strings";

import Button from "components/Button";
import {DateString} from "components/DateInput";
import LoadingIndicator from "components/LoadingIndicator";
import PasswordRequirements from "components/PasswordRequirements/PasswordRequirements";
import StyledDateInput from "components/StyledDateInput";
import StyledModal from "components/StyledModal";
import TextInput from "components/TextInput";
import UnauthLayout from "components/UnauthLayout";

import {AssistedUserErrorResponse} from "liberty-core/api/models";
import {
  useAssistedUser,
  useAssistedUserActivation,
} from "liberty-core/api/react-query/user";

import {useAlert} from "lib/alert";

import styles from "./ExpressRegisterPage.module.css";

const errorFieldNames = {
  password: "Password",
  confirmPassword: "ConfirmPassword",
  dateOfBirth: "DateOfBirth",
};

export default function ExpressRegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [dateOfBirth, setDateOfBirth] = useState<DateString>("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [fieldErrors, setFieldErrors] = useState<{[field: string]: string[]}>(
    {},
  );

  const {mutateAsync: assistUserRequest} = useAssistedUser();
  const {mutateAsync: submitAssistUserActivationRequest} =
    useAssistedUserActivation();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const alert = useAlert();

  const getAssistedUser = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await assistUserRequest(token ?? "");
      setEmail(result.email || "");
    } catch (err) {
      let errText = strings.genericErrorMessage;
      if (err && err instanceof AxiosError && err.response?.data) {
        const {errors, generic} = err.response
          .data as AssistedUserErrorResponse;

        errText = generic ? generic.join("\n") : strings.genericErrorMessage;

        // Combine all validation errors into alert text
        if (err.response?.status === 400 && errors) {
          errText = Object.values(errors).reduce(
            (agg, item) => agg + "\n" + item.join("\n"),
            errText,
          );
        }
      }
      alert({
        title: stringsCommon.whoops,
        message: errText,
        callbackFn: () => navigate("/register"),
      });
    } finally {
      setIsLoading(false);
    }
  }, [assistUserRequest, token, alert, navigate]);

  useEffect(() => {
    getAssistedUser();
  }, [getAssistedUser]);

  const setPasswordVal = (newPassword: string) => {
    setPassword(newPassword);
    setFieldErrors((prev) => ({
      ...prev,
      [errorFieldNames.password]: [],
      [errorFieldNames.confirmPassword]: [],
    }));
  };

  const setConfirmPasswordVal = (newConfirmPassword: string) => {
    setConfirmPassword(newConfirmPassword);
    setFieldErrors((prev) => ({
      ...prev,
      [errorFieldNames.password]: [],
      [errorFieldNames.confirmPassword]: [],
    }));
  };

  const onExpressRegister = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault();
    if (isSubmitting) {
      return;
    }

    let errText = "";
    let validation: AssistedUserErrorResponse["errors"];

    if (password !== confirmPassword) {
      errText = strings.passwordMatchError;
      validation = {
        [errorFieldNames.password]: [strings.mustMatch],
        [errorFieldNames.confirmPassword]: [strings.mustMatch],
      };
    }

    if (errText && validation) {
      alert({
        title: stringsCommon.whoops,
        message: errText,
      });
      setFieldErrors(validation);
      return;
    }

    setIsSubmitting(true);
    try {
      await submitAssistUserActivationRequest({
        verificationCode: token,
        dob: dateOfBirth,
        password: password,
      });

      setShowSuccessModal(true);
    } catch (err) {
      if (err && err instanceof AxiosError && err.response?.data) {
        const {errors, generic} = err.response
          .data as AssistedUserErrorResponse;
        // Pull VerificationCode validation error out of error response in order to
        // Display in alert modal description
        const {VerificationCode, ...restErrors} = errors || {};

        if (err.response?.status === 400 && restErrors) {
          setFieldErrors(restErrors);
        }

        // If generic error present, display that
        // Otherwise, if a VerificationCode validation error is present, display that
        // Otherwise, display a generic error message
        alert({
          title: stringsCommon.whoops,
          message: generic
            ? generic.join("\n")
            : VerificationCode
            ? VerificationCode.join("\n")
            : strings.genericErrorMessage,
        });
      } else {
        alert({
          title: stringsCommon.whoops,
          message: strings.genericErrorMessage,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <UnauthLayout
      rightLink={{label: stringsCommon.login, to: "/login"}}
      leftLink={{label: stringsCommon.cancel, to: "/"}}
    >
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <form className={styles.registerInfo}>
          <Button
            variant="minimal"
            to="/"
            className={styles.cancelButton}
            labelClassName={styles.cancelButtonContent}
          >
            {stringsCommon.cancel}
          </Button>
          <div
            className={classNames(styles.registerText, styles.registerInput)}
          >
            {strings.title}
          </div>
          <div className={classNames(styles.description, styles.registerInput)}>
            {strings.description}
          </div>
          <StyledDateInput
            className={styles.registerInput}
            label={stringsCommon.dateOfBirth}
            autoComplete="bday"
            value={dateOfBirth}
            onChangeDate={(date) => {
              setDateOfBirth(date);
              setFieldErrors((prev) => ({
                ...prev,
                [errorFieldNames.dateOfBirth]: [],
              }));
            }}
            errorText={fieldErrors[errorFieldNames.dateOfBirth]?.join("\n")}
          />
          <TextInput
            className={styles.emailInput}
            label={stringsCommon.email}
            type="email"
            autoComplete="email"
            disabled={true}
            value={email}
          />
          <TextInput
            className={styles.registerInput}
            label={stringsCommon.password}
            type="password"
            autoComplete="new-password"
            value={password}
            onChangeText={setPasswordVal}
            errorText={fieldErrors[errorFieldNames.password]?.join("\n")}
          />
          <TextInput
            className={styles.registerInput}
            label={strings.confirmPassword}
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChangeText={setConfirmPasswordVal}
            errorText={fieldErrors[errorFieldNames.confirmPassword]?.join("\n")}
          />
          <PasswordRequirements password={password} />
          <Button
            className={styles.registerInput}
            variant="filled"
            onClick={onExpressRegister}
            disabled={!password || !confirmPassword}
            loading={isSubmitting}
          >
            {stringsCommon.register}
          </Button>
        </form>
      )}
      <StyledModal
        title={stringsCommon.success}
        visible={showSuccessModal}
        onRequestClose={() => navigate("/")}
      >
        <div>
          <div className={styles.success}>{strings.verifySuccess}</div>
          <Button
            className={styles.successConfirm}
            variant="filled"
            onClick={() => navigate("/login")}
          >
            {strings.continue}
          </Button>
        </div>
      </StyledModal>
    </UnauthLayout>
  );
}
